const adyenStore = require('app_adyen_SFRA/../../../config/store');
const {
  billing: billingConstants,
  paymentMethod: paymentMethodConstants,
} = require('./constants');
const checkoutViewManager = require('../checkoutViewManager');
const formHelpers = require('../formErrors');
const checkoutStageConstants = require('../stage/constants');
const checkoutStageManager = require('../stage/manager');
const addressHelpers = require('../address');
const getAnalyticsEventBus = require('../../common/getAnalyticsEventBus');
const { default: getFrontConfig } = require('../../common/getFrontConfig');
const {
  hideElementsBySelector,
  showElementsBySelector,
} = require('../../common/utils/dom');
const formUtils = require('../../common/utils/formUtils');
const clientSideValidation = require('../../components/clientSideValidation');
const modals = require('../../components/modals');

const analyticsEventBus = getAnalyticsEventBus();
const {
  checkout: { adyenPaymentMethods, paymentMethods },
} = getFrontConfig();
// data-method-id of the Klarna tab (klarnaPaymentsTab.isml), its ONE_KLARNA_ID category
const KLARNA_TAB_METHOD_ID = 'Klarna';

//------------------------------------------------------------------------------
// COMMON HELPERS
//------------------------------------------------------------------------------

/**
 * Checks payment method and billing address to set suitable next stage
 *  @param {boolean} [methodId] Optional payment method Id. If not given, takes it from DOM (active nav-link).
 */
function handlePaymentMethodNextButton(methodId) {
  const currentStage = checkoutStageManager.methods.getStage();
  if (currentStage === checkoutStageConstants.PAYMENT_METHOD) {
    sessionStorage.setItem(
      paymentMethodConstants.MAIN_PAYMENT_SESSION_ATTR,
      methodId || getSelectedPaymentMethodIds()[0] // In PAYMENT_METHOD there is only one selected payment method
    );
  }

  if (currentStage === checkoutStageConstants.SECOND_PAYMENT_METHOD) {
    const selectedPaymentMethodIds = getSelectedPaymentMethodIds();
    if (selectedPaymentMethodIds.length <= 1) {
      // Payment method submit of SECOND_PAYMENT_METHOD must have at least 2 selected payment methods.
      return;
    }
  }

  const lastSelectedPaymentMethodId =
    methodId || getLastSelectedPaymentMethodId();
  processSelectedMethodId(lastSelectedPaymentMethodId);

  if (
    (isAdyenComponentSelected(lastSelectedPaymentMethodId) &&
      adyenSelectedMethodHasPaymentDetails()) ||
    isAssistedShoppingSelected(lastSelectedPaymentMethodId) ||
    isGiftCardSelected(lastSelectedPaymentMethodId)
  ) {
    let nextStage = checkoutStageConstants.PAYMENT_DETAILS;
    if (currentStage === checkoutStageConstants.SECOND_PAYMENT_METHOD) {
      nextStage = checkoutStageConstants.SECOND_PAYMENT_DETAILS;
    }
    checkoutStageManager.methods.setStage(nextStage);
  } else if (isValidBilling()) {
    updateBillingSummaryFromUI();
    checkoutStageManager.methods.setStage(checkoutStageConstants.PLACE_ORDER);
  } else {
    formHelpers.clearPreviousErrors(billingConstants.BILLING_ADDRESS_SELECTOR);
    hideElementsBySelector(
      billingConstants.BILLING_ADDRESS_FORM_ERROR_SELECTOR
    );
    checkoutStageManager.methods.setStage(
      checkoutStageConstants.BILLING_ADDRESS
    );
  }
}

/**
 * Gets the id of the active tab pane that holds the fields of a payment method.
 * Adyen sub-methods (e.g. `AdyenComponent__mbway`) share the `AdyenComponent` pane, and the
 * Klarna tab maps to the `KLARNA_PAYMENTS` pane.
 * Without a match it falls back to the active pane only when it is the only one:
 * in multipayment several panes are active and guessing would submit another method.
 * @param {string} [paymentMethodId] - Payment method id
 * @returns {string|undefined} The tab pane id
 */
function getPaymentTabId(paymentMethodId) {
  const activeTabSelector = '.credit-card-selection-new .tab-pane.active';
  const $activeTabs = $(activeTabSelector);
  if (paymentMethodId) {
    const isAdyenSubMethod = paymentMethodId.startsWith(
      `${paymentMethods.AdyenComponent}__`
    );
    // The Klarna tab is "Klarna" but its pane submits KLARNA_PAYMENTS
    let paneMethodId = paymentMethodId;
    if (isAdyenSubMethod) {
      paneMethodId = paymentMethods.AdyenComponent;
    } else if (paymentMethodId === KLARNA_TAB_METHOD_ID) {
      paneMethodId = paymentMethods.Klarna;
    }
    const $scopedTab = $activeTabs.filter(`:has([value="${paneMethodId}"])`);
    if ($scopedTab.length) {
      return $scopedTab.attr('id');
    }
  }
  return $activeTabs.length === 1 ? $activeTabs.attr('id') : undefined;
}

/**
 * Prepare ajax data for Payment Submit
 * @param {string} [paymentMethodId] - Payment method id to prepare the data for. If not provided, it will be obtained from the DOM.
 * @returns {string} serialized string to send in the ajax call
 */
function preparePaymentData(paymentMethodId) {
  /* Billing address */
  const billingAddressFormBlockSelector = `${billingConstants.BILLING_ADDRESS_FORM_SELECTOR} ${billingConstants.BILLING_ADDRESS_BLOCK_SELECTOR}`;
  let billingAddressForm = $(
    `${billingAddressFormBlockSelector} :input`
  ).serialize();
  $('body').trigger('checkout:serializeBilling', {
    form: $(billingAddressFormBlockSelector),
    data: billingAddressForm,
    callback(data) {
      if (data) {
        billingAddressForm = data;
      }
    },
  });

  /* Contact info */
  const contactInfoFormSelector = `${billingConstants.BILLING_ADDRESS_FORM_SELECTOR} .contact-info-block`;
  let contactInfoForm = $(`${contactInfoFormSelector} :input`).serialize();
  $('body').trigger('checkout:serializeBilling', {
    form: $(contactInfoFormSelector),
    data: contactInfoForm,
    callback(data) {
      if (data) {
        contactInfoForm = data;
      }
    },
  });

  /* Adyen methods without payment details */
  if (
    isAdyenComponentSelected(paymentMethodId) &&
    !adyenSelectedMethodHasPaymentDetails()
  ) {
    setAdyenInputsFromStore();
  }

  /* Adyen card installments */
  if (
    paymentMethodId === paymentMethods.AdyenComponent &&
    window.AdyenInstallmentsEnabled &&
    window.AdyenInstallmentsOptions
  ) {
    let cardInstallments = null;
    const selectedCardInput = $('input[name="brandCode"]:checked');
    if (selectedCardInput.val().indexOf('scheme') !== -1) {
      cardInstallments = $('#adyen-new-card-installments select').val();
    } else if (selectedCardInput.val().indexOf('storedCard') !== -1) {
      cardInstallments = selectedCardInput
        .closest('li')
        .find('.adyen-checkout__custominstallments select')
        .val();
    }
    if (cardInstallments && parseInt(cardInstallments, 10) > 1) {
      const adyenStateDataMod = JSON.parse($('#adyenStateData').val());
      adyenStateDataMod.installments = { value: cardInstallments };
      $('#adyenStateData').val(JSON.stringify(adyenStateDataMod));
    }
  }

  /* Payment info */
  const activeTabId = getPaymentTabId(paymentMethodId);
  const paymentInfoSelector = `${billingConstants.BILLING_ADDRESS_FORM_SELECTOR} .${activeTabId} .payment-form-fields :input`;
  let paymentInfoForm = activeTabId ? $(paymentInfoSelector).serialize() : '';
  $('body').trigger('checkout:serializeBilling', {
    form: $(paymentInfoSelector),
    data: paymentInfoForm,
    callback(data) {
      if (data) {
        paymentInfoForm = data;
      }
    },
  });

  return `${billingAddressForm}&${contactInfoForm}&${paymentInfoForm}`;
}

const commonHelpers = {
  handlePaymentMethodNextButton,
  preparePaymentData,
};

//------------------------------------------------------------------------------
// BILLING HELPERS
//------------------------------------------------------------------------------

/**
 * Gets address properties from the billing-address form
 * @returns {Object} - a JSON object with all values
 */
function getBillingAddressFieldsFromUI() {
  const billingForm = $(billingConstants.BILLING_ADDRESS_SELECTOR);
  const addressObject = {};

  const fields = [
    'firstName',
    'lastName',
    'address1',
    'address2',
    'city',
    'postalCode',
    'stateCode',
    'byl_region',
    'colonia',
    'comunaCode',
    'country',
    'phone',
    'phoneBody',
    'phonePrefix',
  ];

  fields.forEach((field) => {
    const node = $(`[name$=${field}]`, billingForm);
    if (node[0] && node[0].tagName === 'INPUT') {
      addressObject[field] = node.val();
    } else if (node[0] && node[0].tagName === 'SELECT') {
      const nodeName = $(`option[value = "${node.val()}"]`, node).text();
      addressObject[field] =
        field === 'country' ? `(${nodeName.trim()})` : nodeName;
    }
  });

  return addressObject;
}

/**
 * Update billing address summary from address in billing form
 * @param {Object} address - address object from form
 */
function updateBillingSummaryFromUI() {
  const address = getBillingAddressFieldsFromUI();
  const addressContainer = $('.order-summary-billing .address-summary');
  addressHelpers.methods.populateAddressSummary(addressContainer, address);
}

/**
 * Show billing selector and hides billing address block
 * @param {jQuery} billingForm - current billing form
 */
function showOnlyBillingSelectorBlock(billingForm) {
  hideElementsBySelector(
    billingConstants.BILLING_ADDRESS_SELECTOR,
    billingForm[0]
  );
  showElementsBySelector(
    billingConstants.BILLING_ADDRESS_SELECTOR_SELECTOR,
    billingForm[0]
  );
}

/** Validate billing-address-block
 * @returns {boolean} True if valid, false otherwise
 */
function isValidBilling() {
  const billingAddressBlock = document.querySelector(
    billingConstants.BILLING_ADDRESS_BLOCK_SELECTOR
  );
  const billingAddressFields = billingAddressBlock.querySelectorAll(
    'input, select, textarea'
  );
  const paymentForm = billingAddressBlock.closest('.payment-form');

  const paymentFormErrorClass = 'payment-form--form-error';
  const isBillingValid =
    clientSideValidation.functions.validateFields(billingAddressFields);
  if (isBillingValid) {
    hideElementsBySelector(
      billingConstants.BILLING_ADDRESS_FORM_ERROR_SELECTOR
    );
    paymentForm.classList.remove(paymentFormErrorClass);
  } else {
    showElementsBySelector(
      billingConstants.BILLING_ADDRESS_FORM_ERROR_SELECTOR
    );
    paymentForm.classList.add(paymentFormErrorClass);
  }
  return isBillingValid;
}

/**
 * Show billing address block and hides billing selector
 * @param {jQuery} billingForm  - current billing form
 */
function showOnlyBillingAddressBlock(billingForm) {
  hideElementsBySelector(
    billingConstants.BILLING_ADDRESS_SELECTOR_SELECTOR,
    billingForm[0]
  );
  showElementsBySelector(
    billingConstants.BILLING_ADDRESS_SELECTOR,
    billingForm[0]
  );
}

/**
 * Sets the billing address view depending on the selected address option
 */
function setTheBillingAddressView() {
  const billingForm = document.querySelector('.payment-form');
  const $billingForm = $(billingForm);
  const newEl = billingForm.querySelector('.addressSelector input[value=new]');
  if (newEl?.checked) {
    showOnlyBillingAddressBlock($billingForm);
  } else {
    showOnlyBillingSelectorBlock($billingForm);
  }
  checkoutViewManager.methods.changeViewToOnlyForm();
  clientSideValidation.handleSubmitState(billingForm);
}

/**
 * Select new address and show only address form
 * @param {jQuery} $billingForm
 */
function selectNewBillingAddress($billingForm) {
  $('body').trigger('checkout:clearBillingForm');
  const $newEl = $billingForm.find('.addressSelector input[value=new]');
  $newEl.prop('checked', true).trigger('change');
  $billingForm.parents('[data-address-mode]').attr('data-address-mode', 'new');

  showOnlyBillingAddressBlock($billingForm);
}

const billingHelpers = {
  isValidBilling,
  selectNewBillingAddress,
  setTheBillingAddressView,
  showOnlyBillingAddressBlock,
  showOnlyBillingSelectorBlock,
  updateBillingSummaryFromUI,
};

//------------------------------------------------------------------------------
// PAYMENT DETAIL HELPERS
//------------------------------------------------------------------------------

/**
 * Checks if the selected Adyen payment method requires additional payment details to be filled
 * @returns {boolean}
 */
function adyenSelectedMethodHasPaymentDetails() {
  const processedSelectedMethodIds = getSelectedPaymentMethodIds().map(
    (methodId) => methodId.split('__')[1]
  );
  const paymentWithoutDetails = processedSelectedMethodIds.some((methodId) =>
    paymentMethodConstants.ADYEN_PAYMENTS_WITHOUT_DETAILS.includes(methodId)
  );
  return !paymentWithoutDetails;
}

/**
 * Copies the selected Adyen method from the store to the payment form inputs. link_adyen only does it
 * on the payment details submit (adyen/checkout/index.js), a stage that methods without details skip.
 */
function setAdyenInputsFromStore() {
  const paymentMethodNameInput = document.querySelector(
    '#adyenPaymentMethodName'
  );
  if (paymentMethodNameInput) {
    paymentMethodNameInput.value =
      adyenStore.brand ||
      document.querySelector(`#lb_${adyenStore.selectedMethod}`)?.innerHTML;
  }
  const stateDataInput = document.querySelector('#adyenStateData');
  if (stateDataInput) {
    stateDataInput.value = JSON.stringify(adyenStore.stateData);
  }
}

/**
 * Toggles Adyen payment method content visibility
 * @param {string} paymentMethodType - payment method type to show
 */
function toggleAdyenPaymentMethodContent(paymentMethodType) {
  let firstVisiblePayment;
  const adyenPaymentMethodElements = document.querySelectorAll(
    '#adyen-component-content .adyen-payment-method-type'
  );
  if (!adyenPaymentMethodElements.length) {
    return;
  }

  adyenPaymentMethodElements.forEach((adyenPaymentMethod) => {
    const paymentMethodValue =
      adyenPaymentMethod.querySelector('input')?.value || '';
    const isMatchingPaymentMethodType =
      paymentMethodValue === paymentMethodType;
    const isStoredCardElement = paymentMethodValue.includes('storedCard');
    const isCreditCardSelected =
      paymentMethodType === adyenPaymentMethods.CREDIT_CARD.type;
    const shouldShow =
      isMatchingPaymentMethodType ||
      (isCreditCardSelected && isStoredCardElement);

    adyenPaymentMethod.classList.toggle('d-none', !shouldShow);

    if (shouldShow && !firstVisiblePayment) {
      firstVisiblePayment = adyenPaymentMethod;
    }
  });

  if (firstVisiblePayment) {
    firstVisiblePayment.querySelector('input').click();
  }
}

/**
 * Checks selected Adyen payment method to show
 * corresponding content
 * @param {string} selectedMethodId
 */
function handleToggleAdyenPaymentMethodContent(selectedMethodId) {
  // Double underscore is used in Adyen custom methods to separate "AdyenComponent"
  // and specific payment method name
  if (selectedMethodId.includes('__')) {
    const selectedMethodIdSuffix = selectedMethodId.split('__')[1];
    toggleAdyenPaymentMethodContent(selectedMethodIdSuffix);
  } else {
    toggleAdyenPaymentMethodContent(adyenPaymentMethods.CREDIT_CARD.type);
  }
}

/**
 * Validates if AdyenComponent is selected and valid
 * @returns {boolean} True if AdyenComponent is selected and valid
 */
function isAdyenComponentValid() {
  if (!isAdyenComponentSelected()) return false;
  const adyenCheckout = require('on4u_adyen_SFRA/adyen/checkout/helpers');
  return adyenCheckout.showValidation();
}

/**
 * Validate MBWay inputs
 */
function processMBWayInputs() {
  const mbWayContainer = document.querySelector('.adyen-checkout__mb-way');
  const phoneNumberInput = mbWayContainer?.querySelector(
    '.adyen-checkout-phone-number input'
  );
  if (phoneNumberInput && phoneNumberInput.value !== '') {
    isAdyenComponentValid();
  }
}

/**
 * Process the selected payment method id to show/hide the corresponding content
 * @param {string} selectedMethodId - selected payment method id
 */
function processSelectedMethodId(selectedMethodId) {
  if (!selectedMethodId || selectedMethodId === null) {
    return;
  }
  const paypalContentSelector = '.tab-pane.paypal-content';
  const applepayContentSelector = '.tab-pane.applepay-content';
  const submitPaymentBtnSelector = '.place-order-btn';
  const paymentDetailsNextStageBtnParent = document
    .querySelector('.payment-details__next-stage')
    .closest('.form__actions');
  const $paypalContent = $(paypalContentSelector);
  const processedSelectedMethod = selectedMethodId.split('__');
  const processedMethodId = processedSelectedMethod[0];
  const processedMethodSuffix = processedSelectedMethod[1];

  $('#selectedPaymentOption').val(processedMethodId);

  togglePaymentMethodsTabs(selectedMethodId);

  // Hide invalid card error
  const paymentErrorCode = new URLSearchParams(window.location.search).get(
    'paymentErrorCode'
  );
  if (!paymentErrorCode) {
    $('#invalidCardDetails').hide();
  }

  if (selectedMethodId === paymentMethods.PAYPAL) {
    hideElementsBySelector(submitPaymentBtnSelector);
    hideElementsBySelector(applepayContentSelector);
    showElementsBySelector(paypalContentSelector);
    $paypalContent.find('input, textarea, select').removeAttr('disabled');
  } else if (selectedMethodId === paymentMethods.APPLE_PAY) {
    hideElementsBySelector(paypalContentSelector);
    hideElementsBySelector(submitPaymentBtnSelector);
    showElementsBySelector(applepayContentSelector);
    $paypalContent.find('input, textarea, select').attr('disabled', 'disabled');
  } else {
    if (processedMethodSuffix === adyenPaymentMethods.MB_WAY.type) {
      processMBWayInputs();
    }
    if (processedMethodSuffix === adyenPaymentMethods.GOOGLE_PAY.type) {
      paymentDetailsNextStageBtnParent.classList.add('d-none');
    } else {
      paymentDetailsNextStageBtnParent.classList.remove('d-none');
    }
    hideElementsBySelector(paypalContentSelector);
    hideElementsBySelector(applepayContentSelector);
    showElementsBySelector(submitPaymentBtnSelector);
    $paypalContent.find('input, textarea, select').attr('disabled', 'disabled');
  }
}

/**
 * Validates if assisted shopping payment method form is valid
 * @returns {boolean} True if assisted shopping payment method form is valid
 */
function isValidAssistedShopping() {
  const selectedPaymentMethodIds = getSelectedPaymentMethodIds();

  let selectedPaymentMethodContainer;
  if (selectedPaymentMethodIds.includes(paymentMethods.ADYEN_PAYBYLINK)) {
    selectedPaymentMethodContainer = document.querySelector(
      '.pay-by-link-content'
    );
  } else if (selectedPaymentMethodIds.includes(paymentMethods.ADYEN_POS)) {
    selectedPaymentMethodContainer =
      document.querySelector('.adyen-POS-content');
  } else if (
    selectedPaymentMethodIds.includes(paymentMethods.MERCADOPAGO_PAYBYLINK)
  ) {
    selectedPaymentMethodContainer = document.querySelector(
      '.pay-by-link-content'
    );
  } else if (
    selectedPaymentMethodIds.includes(paymentMethods.MERCADOPAGO_POS)
  ) {
    selectedPaymentMethodContainer = document.querySelector(
      '.mercadopago-pos-content'
    );
  } else {
    return false;
  }

  const inputsToValidate =
    selectedPaymentMethodContainer.querySelectorAll('input, select');
  return clientSideValidation.functions.validateFields(inputsToValidate, false);
}

/**
 * Checks if there is a valid payment.
 * For Adyen and Assisted Shopping, validates additional fields.
 * Else, if any payment is selected.
 * @return {boolean} is valid Payment
 */
function isValidPayment() {
  if (isAdyenComponentSelected() && isAdyenComponentValid()) {
    return true;
  }
  if (isAssistedShoppingSelected() && isValidAssistedShopping()) {
    return true;
  }

  const isPaymentSelected = document.querySelector(
    '.payment-options .nav-link.active'
  );
  return !!isPaymentSelected;
}

/**
 * SFRA function
 * clears the credit card form
 */
function clearCreditCardForm() {
  $('input[name$="_cardNumber"]').data('cleave')?.setRawValue('');
  $('select[name$="_expirationMonth"]').val('');
  $('select[name$="_expirationYear"]').val('');
  $('input[name$="_securityCode"]').val('');

  // Remove errors
  $('#invalidCardDetails').hide();
  $('.credit-card-selection-new')
    .find('.adyen-checkout__input--error')
    .removeClass('adyen-checkout__input--error');
}

/**
 * Show MercadoPago form errors if any of its fields is empty
 */
function handleMercadoPagoFormErrors() {
  if ($('#mercadopago-content').length === 0) {
    return;
  }

  const $mercadoPagoEmail = $('#mercadoPagoEmail');
  const $mercadoPagoPhone = $('#mercadoPagoPhoneNumber');
  if ($mercadoPagoEmail.val() === '' || $mercadoPagoPhone.val() === '') {
    $mercadoPagoEmail.closest('.form__fields-row').removeClass('d-none');
  }
}

/**
 * After the payment submit ajax call, process the response
 * @param  {Object} data - ajax response data
 */
function handlePaymentSubmitResponse(data) {
  if (data.error) {
    if (data.showModal) {
      modals.createCustomModal(
        data.modalHtml,
        { backdrop: 'static' },
        data.includeAssets
      );
      return;
    }

    // TODO check errors and go to suitable stage
    // First the stage: changing it removes the checkout error
    checkoutStageManager.methods.setStage(
      checkoutStageConstants.PAYMENT_DETAILS
    );

    const { errorMessage } = data;
    formHelpers.addCheckoutError(errorMessage);

    if (data.fieldErrors?.length) {
      data.fieldErrors.forEach((error) => {
        if (Object.keys(error).length) {
          formUtils.loadFormErrors('.payment-form', error);
        }
      });
    }

    if (data.serverErrors?.length) {
      data.serverErrors.forEach((error) => {
        const invalidCardError = $('#invalidCardDetails');
        invalidCardError.text(error);
        invalidCardError.show();
      });
    }

    handleMercadoPagoFormErrors();

    $('.payment-form').addClass('payment-form--form-error');

    if (data.cartError) {
      window.location.href = data.redirectUrl;
    }
  } else {
    $('body').trigger('checkout:payment-saved', data.order);
    // Send info for Analytics
    analyticsEventBus.publish(
      analyticsEventBus.EVENTS.checkoutPaymentInfoAdded,
      {
        order: data.order,
      }
    );
  }
}

/**
 * Removes payment error code from URL
 */
function clearPaymentErrorFromURL() {
  const { searchParams } = new URL(window.location.href);
  searchParams.delete('paymentErrorCode');
  searchParams.delete(paymentMethodConstants.PAYMENT_ERROR_URL_PARAM);
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}?${searchParams.toString()}`
  );
}

const paymentDetailHelpers = {
  clearCreditCardForm,
  clearPaymentErrorFromURL,
  handlePaymentSubmitResponse,
  handleToggleAdyenPaymentMethodContent,
  adyenSelectedMethodHasPaymentDetails,
  isAdyenComponentValid,
  isValidAssistedShopping,
  isValidPayment,
  processSelectedMethodId,
};

//------------------------------------------------------------------------------
// PAYMENT METHOD HELPERS
//------------------------------------------------------------------------------

/**
 * Checks if AdyenComponent is selected as payment method.
 * @param {string} [methodId] Optional payment method Id. If not given, takes it from DOM.
 * @returns {boolean} True if AdyenComponent is selected as payment method.
 */
function isAdyenComponentSelected(methodId) {
  const selectedPaymentMethodIds = methodId
    ? [methodId]
    : getSelectedPaymentMethodIds().map(
        (paymentMethodId) => paymentMethodId.split('__')[0]
      );
  return selectedPaymentMethodIds.some((id) =>
    id.includes(paymentMethods.AdyenComponent)
  );
}

/**
 * Checks if an assisted shopping payment method is selected.
 * @param {string} [methodId] Optional payment method Id. If not given, takes it from DOM.
 * @returns {boolean} True if an assisted shopping payment method is selected.
 */
function isAssistedShoppingSelected(methodId) {
  const selectedPaymentMethodIds = methodId
    ? [methodId]
    : getSelectedPaymentMethodIds();
  return (
    selectedPaymentMethodIds.includes(paymentMethods.ADYEN_PAYBYLINK) ||
    selectedPaymentMethodIds.includes(paymentMethods.ADYEN_POS) ||
    selectedPaymentMethodIds.includes(paymentMethods.MERCADOPAGO_PAYBYLINK) ||
    selectedPaymentMethodIds.includes(paymentMethods.MERCADOPAGO_POS)
  );
}

/**
 * Checks if Gift Card payment method is selected
 * @param {string} [methodId] Optional payment method Id. If not given, takes it from DOM.
 * @returns {boolean} True if the Gift card payment method is selected
 */
function isGiftCardSelected(methodId) {
  const selectedPaymentMethodIds = methodId
    ? [methodId]
    : getSelectedPaymentMethodIds();
  return selectedPaymentMethodIds.includes(paymentMethods.GIFT_CARD);
}

/**
 * Toggles gift card content visibility and its fields' required state.
 * On the SECOND_PAYMENT_METHOD stage the content is kept active but hidden so
 * it is still sent in SubmitPayment; required is only enforced while visible.
 * @param {HTMLElement} giftCardTab - gift card nav-link element
 * @param {HTMLElement} giftCardContent - gift card content element
 */
function toggleGiftCardContent(giftCardTab, giftCardContent) {
  const ACTIVE_CLASS = 'active';
  const isSecondPaymentMethodStage =
    checkoutStageManager.methods.getStage() ===
    checkoutStageConstants.SECOND_PAYMENT_METHOD;
  if (isSecondPaymentMethodStage) {
    giftCardTab.classList.add(ACTIVE_CLASS);
    // Hide gift card content but keep active to ensure being sent in SubmitPayment
    giftCardContent.classList.add(ACTIVE_CLASS, 'd-none');
  } else {
    giftCardContent.classList.remove('d-none');
  }
  // Enforce required only while the gift card fields are visible
  const giftCardIsVisible =
    giftCardContent.classList.contains(ACTIVE_CLASS) &&
    !giftCardContent.classList.contains('d-none');
  giftCardContent
    .querySelectorAll('#giftcardNumber, #giftcardCvv')
    .forEach((field) => {
      if (giftCardIsVisible) {
        field.setAttribute('required', 'required');
      } else {
        field.removeAttribute('required');
      }
    });
}

/**
 * Toggles the active state of payment method tabs and their content
 * @param {string} selectedMethodId - selected payment method id
 */
function togglePaymentMethodsTabs(selectedMethodId) {
  const ACTIVE_CLASS = 'active';
  const paymentMethodsTabs = document.querySelectorAll(
    '.payment-options .nav-link'
  );
  const selectedMethodTab = document.querySelector(
    `.payment-options [data-method-id="${selectedMethodId}"] .nav-link`
  );
  const giftCardTab = document.querySelector(
    `.payment-options [data-method-id="${paymentMethods.GIFT_CARD}"] .nav-link`
  );
  const giftCardContent =
    giftCardTab && document.querySelector(giftCardTab.getAttribute('href'));

  const paymentInformation = document.querySelector('.payment-information');
  if (paymentInformation) {
    paymentInformation.dataset.paymentMethodId = selectedMethodId;
  }

  // Deactivate all tabs and their content
  paymentMethodsTabs.forEach((tab) => {
    tab.classList.remove(ACTIVE_CLASS);
    const tabContent = document.querySelector(tab.getAttribute('href'));
    tabContent?.classList.remove(ACTIVE_CLASS);
  });

  // Activate selected tab and its content
  if (selectedMethodTab) {
    selectedMethodTab.classList.add(ACTIVE_CLASS);
    const selectedMethodContent = document.querySelector(
      selectedMethodTab.getAttribute('href')
    );
    selectedMethodContent?.classList.add(ACTIVE_CLASS);
  }

  // Specific logic for gift card contents
  if (giftCardTab && giftCardContent) {
    toggleGiftCardContent(giftCardTab, giftCardContent);
  }

  if (isAdyenComponentSelected() && selectedMethodId) {
    handleToggleAdyenPaymentMethodContent(selectedMethodId);
  }
}

/**
 * Selects the payment method saved in session,
 * and calls nextButton handler to set proper checkout stage
  @param {string} sessionSelectedMethod Selected payment method saved in session
*/
function selectSessionPaymentMethodAndContinue(sessionSelectedMethod) {
  const savedMethodButtonelement = document.querySelector(
    `.payment-options .nav-item[data-method-id="${sessionSelectedMethod}"] .nav-link`
  );
  $(savedMethodButtonelement).trigger('click');
  handlePaymentMethodNextButton(sessionSelectedMethod);
}

/**
 * Get selected payment method ids from the DOM, in the order they must be submitted.
 * The gift card always goes first: its Handle clears every payment instrument of the basket,
 * and calculatePaymentTransaction charges the balance to the first instrument created.
 * @returns {string[]} An array of string containing the payment method ids
 */
function getSelectedPaymentMethodIds() {
  const activePaymentTabs = document.querySelectorAll(
    '.payment-options .nav-item .nav-link.active'
  );
  const selectedPaymentMethodIds = [];
  activePaymentTabs.forEach((tab) => {
    selectedPaymentMethodIds.push(tab.parentNode.dataset.methodId);
  });

  const giftCardIndex = selectedPaymentMethodIds.indexOf(
    paymentMethods.GIFT_CARD
  );
  if (giftCardIndex > 0) {
    // Move the gift card payment method to the first position of the array
    selectedPaymentMethodIds.unshift(
      selectedPaymentMethodIds.splice(giftCardIndex, 1)[0]
    );
  }

  return selectedPaymentMethodIds;
}

/**
 * Gets the last selected payment method id.
 * If there is only one selected payment method, returns that one.
 * @returns {string|null} The last selected payment method id or null if there is no selected payment method
 */
function getLastSelectedPaymentMethodId() {
  const selectedPaymentMethodIds = getSelectedPaymentMethodIds();

  if (selectedPaymentMethodIds.length === 1) {
    return selectedPaymentMethodIds[0];
  }

  const sessionMainPaymentMethod = sessionStorage.getItem(
    paymentMethodConstants.MAIN_PAYMENT_SESSION_ATTR
  );
  return (
    selectedPaymentMethodIds.find(
      (methodId) => methodId !== sessionMainPaymentMethod
    ) || null
  );
}

const paymentMethodHelpers = {
  getSelectedPaymentMethodIds,
  getLastSelectedPaymentMethodId,
  isAdyenComponentSelected,
  isAssistedShoppingSelected,
  isGiftCardSelected,
  selectSessionPaymentMethodAndContinue,
  togglePaymentMethodsTabs,
};

//------------------------------------------------------------------------------
// EXPORTS
//------------------------------------------------------------------------------

module.exports = {
  methods: {
    ...billingHelpers,
    ...commonHelpers,
    ...paymentDetailHelpers,
    ...paymentMethodHelpers,
  },
};
